import { useState, useEffect, useCallback } from "react";
import { useTheme } from "./useTheme";

// =============================================================================
// useGlassStyle — Glass Card Variation Selector (Light Mode Only)
// =============================================================================
// Glass A (light/white gradient) is the only variant for authenticated routes.
// Public routes can force dark via forceLightMode: false → Glass E (smoke).
// =============================================================================

export type GlassVariant = "A" | "E" | "G";
export type GlassMode = GlassVariant | "auto";

const GLASS_CHANGE_EVENT = "altara-glass-change";

// CSS custom property values for each variant
const glassCSS: Record<
  GlassVariant,
  {
    background: string;
    blur: string;
    saturate: string;
    border: string;
    borderTop: string;
    shadow: string;
  }
> = {
  A: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)",
    blur: "12px",
    saturate: "120%",
    border: "1px solid rgba(255,255,255,0.2)",
    borderTop: "1px solid rgba(255,255,255,0.2)",
    shadow: "0 8px 32px rgba(0,0,0,0.2)",
  },
  E: {
    background: "linear-gradient(180deg, rgba(30,30,40,0.4) 0%, rgba(20,20,30,0.3) 100%)",
    blur: "16px",
    saturate: "120%",
    border: "1px solid rgba(255,255,255,0.06)",
    borderTop: "1px solid rgba(255,255,255,0.06)",
    shadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)",
  },
  G: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)",
    blur: "40px",
    saturate: "180%",
    border: "1px solid rgba(255,255,255,0.18)",
    borderTop: "1px solid rgba(255,255,255,0.3)",
    shadow: "0 8px 32px rgba(0,0,0,0.15)",
  },
};

// Style definitions for direct use (preview cards)
export const glassStyles: Record<GlassVariant, React.CSSProperties> = {
  A: {
    background: glassCSS.A.background,
    backdropFilter: `blur(${glassCSS.A.blur}) saturate(${glassCSS.A.saturate})`,
    WebkitBackdropFilter: `blur(${glassCSS.A.blur}) saturate(${glassCSS.A.saturate})`,
    border: glassCSS.A.border,
    boxShadow: glassCSS.A.shadow,
  },
  E: {
    background: glassCSS.E.background,
    backdropFilter: `blur(${glassCSS.E.blur}) saturate(${glassCSS.E.saturate})`,
    WebkitBackdropFilter: `blur(${glassCSS.E.blur}) saturate(${glassCSS.E.saturate})`,
    border: glassCSS.E.border,
    boxShadow: glassCSS.E.shadow,
  },
  G: {
    background: glassCSS.G.background,
    backdropFilter: `blur(${glassCSS.G.blur}) saturate(${glassCSS.G.saturate})`,
    WebkitBackdropFilter: `blur(${glassCSS.G.blur}) saturate(${glassCSS.G.saturate})`,
    border: glassCSS.G.border,
    borderTop: glassCSS.G.borderTop,
    boxShadow: glassCSS.G.shadow,
  },
};

// Apply CSS custom properties to :root
function applyGlassCSS(variant: GlassVariant) {
  const root = document.documentElement;
  const css = glassCSS[variant];
  root.style.setProperty("--glass-background", css.background);
  root.style.setProperty("--glass-blur", css.blur);
  root.style.setProperty("--glass-saturate", css.saturate);
  root.style.setProperty("--glass-filter", `blur(${css.blur}) saturate(${css.saturate})`);
  root.style.setProperty("--glass-border", css.border);
  root.style.setProperty("--glass-border-top", css.borderTop);
  root.style.setProperty("--glass-shadow", css.shadow);
}

export const glassLabels: Record<GlassVariant, string> = {
  A: "Standard",
  E: "Smoke",
  G: "iOS",
};

export const useGlassStyle = (options?: { forceLightMode?: boolean }) => {
  const { isLightMode: themeIsLight } = useTheme();
  // Public routes pass forceLightMode: false to keep dark glass on dark backgrounds
  const isLightMode = options?.forceLightMode !== undefined ? options.forceLightMode : themeIsLight;

  const [mode] = useState<GlassMode>("auto");
  const effectiveVariant: GlassVariant = isLightMode ? "A" : "E";

  // Apply .light-mode class on <html> — single source of truth for text color CSS vars
  useEffect(() => {
    const root = document.documentElement;
    if (isLightMode) {
      root.classList.add("light-mode");
    } else {
      root.classList.remove("light-mode");
    }
  }, [isLightMode]);

  // Apply CSS custom properties whenever effective variant changes
  useEffect(() => {
    applyGlassCSS(effectiveVariant);
  }, [effectiveVariant]);

  // Listen for changes from other components (legacy support)
  useEffect(() => {
    const handleChange = () => {
      applyGlassCSS(effectiveVariant);
    };

    window.addEventListener(GLASS_CHANGE_EVENT, handleChange as EventListener);
    return () => {
      window.removeEventListener(GLASS_CHANGE_EVENT, handleChange as EventListener);
    };
  }, [effectiveVariant]);

  // Legacy setters (no-ops)
  const setGlassMode = useCallback((_newMode: GlassMode) => {}, []);
  const setGlassVariant = useCallback((_newVariant: GlassVariant) => {}, []);

  return {
    mode,
    variant: effectiveVariant,
    setGlassMode,
    setGlassVariant,
    style: glassStyles[effectiveVariant],
    label: glassLabels[effectiveVariant],
    isAuto: true,
  };
};
